/**
 * apiClient - SPA fetch wrapper for the CDX Pickups API.
 *
 * Every /api/pickups/* call carries the Entra access token in the X-Cdx-Bearer CUSTOM header
 * (Azure SWA rewrites the standard Authorization header on managed-functions routes - prod-
 * confirmed on the portal 2026-06-12). On a 401 the client recovers with exactly ONE interactive
 * re-auth per session (bounded); a second 401 surfaces a visible error (AuthErrorBanner) instead
 * of a redirect loop. Ported from the Printing Module apiClient.
 */
import { msalInstance, ensureMsalInitialized, apiTokenRequest, isDevAuthBypass } from '@/auth/index';

function getCdxAccount() {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/** Acquire an Entra access token for this app's API scope. Returns null when unavailable. */
export async function getApiToken(): Promise<string | null> {
  if (isDevAuthBypass()) return null;
  try {
    await ensureMsalInitialized();
    const account = getCdxAccount();
    if (!account) return null;
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken || null;
  } catch {
    // Silent acquisition failed (interaction_required, blocked iframe, expired SSO). Degrade and
    // let the 401 interceptor drive interactive recovery; never redirect from here.
    return null;
  }
}

function withHeader(init: RequestInit | undefined, name: string, value: string): Headers {
  const headers = new Headers(init?.headers);
  headers.set(name, value);
  return headers;
}

/* -------------------------------------------------------------------------- *
 * Visible auth-error state: when the ONE permitted interactive re-auth has been
 * spent and a gated call 401s again, the user gets an explicit error surface
 * (AuthErrorBanner) instead of another redirect. useSyncExternalStore-compatible.
 * -------------------------------------------------------------------------- */
let authErrorState = false;
const authErrorListeners = new Set<() => void>();
function setAuthError(next: boolean): void {
  if (next === authErrorState) return;
  authErrorState = next;
  for (const l of authErrorListeners) l();
}
export function subscribeAuthError(listener: () => void): () => void {
  authErrorListeners.add(listener);
  return () => {
    authErrorListeners.delete(listener);
  };
}
export function getAuthErrorSnapshot(): boolean {
  return authErrorState;
}

const REAUTH_GUARD_KEY = 'pickups_api_reauth_attempted';
let reauthInFlight = false;

/** Named explicit reset (the error surface's Try-again). Re-arms the single re-auth. */
export function resetReauthGuard(): void {
  try {
    sessionStorage.removeItem(REAUTH_GUARD_KEY);
  } catch {
    /* ignore */
  }
  setAuthError(false);
}

async function reauthOnce(): Promise<void> {
  if (reauthInFlight) return;
  let alreadySpent = false;
  try {
    alreadySpent = sessionStorage.getItem(REAUTH_GUARD_KEY) === '1';
  } catch {
    /* ignore */
  }
  if (alreadySpent) {
    // Second 401 after the one permitted redirect: visible error, no redirect.
    setAuthError(true);
    return;
  }
  reauthInFlight = true;
  try {
    await ensureMsalInitialized();
    const account = getCdxAccount();
    if (!account) {
      // Pre-login (fresh tab): the login wall owns sign-in. A 401 before sign-in is expected, not
      // a failure - do NOT spend the one-shot or banner it.
      return;
    }
    try {
      sessionStorage.setItem(REAUTH_GUARD_KEY, '1');
    } catch {
      /* ignore */
    }
    await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
  } catch {
    setAuthError(true);
  } finally {
    reauthInFlight = false;
  }
}

/** The standard fetch wrapper for ALL /api/* calls. */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  opts?: { skipReauth?: boolean },
): Promise<Response> {
  const token = await getApiToken();
  const headers = token ? withHeader(init, 'X-Cdx-Bearer', token) : new Headers(init?.headers);
  const res = await fetch(input, { ...init, headers, credentials: 'include' });
  if (!isDevAuthBypass() && res.status === 401 && !opts?.skipReauth) {
    void reauthOnce();
  }
  return res;
}

/** apiFetch + JSON parse. Throws an Error with `.status` on a non-2xx response. */
export async function apiJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const res = await apiFetch(input, init);
  if (!res.ok) {
    const err = new Error(`API ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export interface PostResult<T> {
  ok: boolean;
  status: number;
  /** Parsed body (the typed success shape, or an error shape like { error, bucket, blocked }). */
  data: (T & Record<string, unknown>) | (Record<string, unknown> & { error?: string });
}

/**
 * POST JSON and return the parsed body WITHOUT throwing on a non-2xx. The pickup flows need the
 * body of a 409 (ineligible / prod-gate) and a 200-with-blocked (SMS do-not-solicit / no-phone)
 * to drive the UI, so this surfaces { ok, status, data } rather than throwing like apiJson.
 */
export async function apiPost<T>(input: RequestInfo | URL, body: unknown): Promise<PostResult<T>> {
  const res = await apiFetch(input, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data: PostResult<T>['data'];
  try {
    data = (await res.json()) as PostResult<T>['data'];
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
}
